import React, { useState, useEffect, useContext } from 'react';
import { OrganizationContext } from '../contexts/OrganizationContext';
import { getCallSummariesByOrganization, getContacts } from '../services/api';
import { format } from 'date-fns';
import { 
  ChevronDown, 
  ChevronUp, 
  Clock, 
  Calendar, 
  DollarSign,
  Phone,
  MessageSquare,
  FileText,
  BarChart,
  ArrowUpDown,
  User,
  Bot,
  CheckCircle,
  XCircle,
  Database,
  TrendingUp
} from 'lucide-react';

// Import our themed audio player component
import ThemedAudioPlayer from './ThemedAudioPlayer';

const ThemedCallSummary = () => {
  const { organizationId } = useContext(OrganizationContext);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    month: '_all',
    year: '_all',
    assistantId: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'callTrack.startTime',
    direction: 'desc'
  });
  const [contacts, setContacts] = useState([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const years = Array.from(
    { length: new Date().getFullYear() - 2019 + 1 },
    (_, i) => (2020 + i).toString()
  );

  useEffect(() => {
    if (organizationId) {
      fetchSummaries();
      fetchContacts();
    }
  }, [organizationId, currentPage, filters, sortConfig]);

  const fetchSummaries = async () => {
    setLoading(true);
    try {
      const response = await getCallSummariesByOrganization(organizationId, {
        month: filters.month === '_all' ? '' : filters.month,
        year: filters.year === '_all' ? '' : filters.year,
        assistantId: filters.assistantId,
        page: currentPage,
        size: 10,
        sortBy: sortConfig.key,
        sortDirection: sortConfig.direction
      });
      
      if (response?.content) {
        console.log("summary="+JSON.stringify(response?.content));
        setSummaries(response.content);
        setTotalPages(response.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching call summaries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await getContacts(organizationId, 0, 1000);
      if (response?.content) {
        setContacts(response.content);
      }
      setContactsLoaded(true);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContactsLoaded(true);
    }
  };

  const getContactName = (phoneNumber) => {
    if (!phoneNumber || !contactsLoaded) return null;
    
    // Normalize both formats to pure digits for comparison
    const normalizeToDigitsOnly = (phone) => {
      if (!phone) return '';
      // Remove all non-digit characters
      return phone.replace(/\D/g, '');
    };
    
    // Get the last 9-10 digits for comparison (ignoring country codes)
    const getLastDigits = (phone, length = 9) => {
      const digitsOnly = normalizeToDigitsOnly(phone);
      return digitsOnly.slice(-length);
    };
    
    // Compare the last digits of phone numbers (ignoring country code differences)
    const phoneLastDigits = getLastDigits(phoneNumber);
    
    // Look for contact with matching phone number (last digits)
    const contact = contacts.find(c => {
      const contactLastDigits = getLastDigits(c.phone);
      return phoneLastDigits === contactLastDigits && phoneLastDigits.length >= 9;
    });
    
    return contact ? contact.name : null;
  };

  const handleSortChange = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1);
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="ml-2 h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="ml-2 h-4 w-4 text-blue-400" />
    ) : (
      <ChevronDown className="ml-2 h-4 w-4 text-blue-400" />
    );
  };

  const formatDuration = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    const secs = Math.floor((minutes * 60) % 60);
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCost = (cost) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(cost);
  };

  const renderSuccessEvaluation = (evaluation) => {
    if (evaluation === null || evaluation === undefined) {
      return <span className="text-gray-500 text-xs">N/A</span>;
    }
    return evaluation ? (
      <div className="flex items-center gap-1 text-green-400">
        <CheckCircle className="h-4 w-4" />
        <span className="text-xs">Success</span>
      </div>
    ) : (
      <div className="flex items-center gap-1 text-red-400">
        <XCircle className="h-4 w-4" />
        <span className="text-xs">Failed</span>
      </div>
    );
  };

  return (
    <div className="bg-gray-900 rounded-xl shadow-lg">
      {/* Header */}
      <div className="p-6 border-b border-gray-800">
        <h2 className="text-xl font-semibold text-gray-100 flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-blue-400" />
          Call Summaries
        </h2>
      </div>

      {/* Filters */}
      <div className="p-6 border-b border-gray-800">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Assistant ID
            </label>
            <input
              value={filters.assistantId}
              onChange={(e) => setFilters(prev => ({ ...prev, assistantId: e.target.value }))}
              placeholder="Enter Assistant ID"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Month
            </label>
            <select
              value={filters.month}
              onChange={(e) => setFilters(prev => ({ ...prev, month: e.target.value }))}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="_all">All Months</option>
              {months.map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Year
            </label>
            <select
              value={filters.year}
              onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="_all">All Years</option>
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                <div 
                  className="flex items-center cursor-pointer" 
                  onClick={() => handleSortChange('callTrack.startTime')}
                >
                  Date {getSortIcon('callTrack.startTime')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                <div 
                  className="flex items-center cursor-pointer" 
                  onClick={() => handleSortChange('callTrack.assistantResponse.vapiAssistant.name')}
                >
                  Assistant {getSortIcon('callTrack.assistantResponse.vapiAssistant.name')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                <div 
                  className="flex items-center cursor-pointer" 
                  onClick={() => handleSortChange('callTrack.customerPhoneNumber')}
                >
                  Customer {getSortIcon('callTrack.customerPhoneNumber')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                <div 
                  className="flex items-center cursor-pointer" 
                  onClick={() => handleSortChange('callTrack.durationInMinutes')}
                >
                  Duration {getSortIcon('callTrack.durationInMinutes')}
                </div>
              </th>
{/*               <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                <div 
                  className="flex items-center cursor-pointer" 
                  onClick={() => handleSortChange('callTrack.cost')}
                >
                  Cost {getSortIcon('callTrack.cost')}
                </div>
              </th>
 */}              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                <div 
                  className="flex items-center cursor-pointer" 
                  onClick={() => handleSortChange('callTrack.callType')}
                >
                  Type {getSortIcon('callTrack.callType')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                <div 
                  className="flex items-center cursor-pointer" 
                  onClick={() => handleSortChange('analysisSuccessEvaluation')}
                >
                  Analysis Status {getSortIcon('analysisSuccessEvaluation')}
                </div>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {loading ? (
              <tr>
                <td colSpan="8" className="px-6 py-4 text-center text-gray-400">
                  Loading summaries...
                </td>
              </tr>
            ) : summaries.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-4 text-center text-gray-400">
                  No call summaries available
                </td>
              </tr>
            ) : (
              summaries.map((summary) => {
                const customerName = getContactName(summary.callTrack.customerPhoneNumber);
                return (
                  <tr key={summary.id} className="hover:bg-gray-800/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-gray-100">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span>{format(new Date(summary.callTrack.startTime), 'PP')}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-gray-100">
                        <Bot className="h-4 w-4 text-gray-400" />
                        <span>{summary.callTrack.assistantResponse?.vapiAssistant?.name || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-gray-100">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span>{summary.callTrack.customerPhoneNumber || 'N/A'}</span>
                        {customerName && (
                          <span className="ml-2 text-sm px-2 py-1 rounded-full bg-blue-500/20 text-blue-300 flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {customerName}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-gray-100">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span>{formatDuration(summary.callTrack.durationInMinutes)}</span>
                      </div>
                    </td>
{/*                     <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-gray-100">
                        <DollarSign className="h-4 w-4 text-gray-400" />
                        <span>{formatCost(summary.callTrack.cost)}</span>
                      </div>
                    </td>
 */}                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-sm rounded-full bg-blue-500/20 text-blue-400">
                        {summary.callTrack.callType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderSuccessEvaluation(summary.analysisSuccessEvaluation)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => setSelectedSummary(
                          selectedSummary?.id === summary.id ? null : summary
                        )}
                        className="px-3 py-1 text-sm rounded-md bg-gray-800 text-gray-100 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {selectedSummary?.id === summary.id ? 'Hide Details' : 'View Details'}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Selected Summary Details */}
      {selectedSummary && (
        <div className="border-t border-gray-800 p-6">
          <div className="space-y-4">
            {/* Customer Details */}
            <div className="bg-gray-800 rounded-lg overflow-hidden">
              <button
                onClick={() => setSelectedSummary(null)}
                className="w-full px-4 py-2 text-left text-gray-100 hover:bg-gray-700 flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Customer Details
                </span>
                <ChevronUp className="h-4 w-4" />
              </button>
              <div className="px-4 py-2 text-gray-300">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">Phone:</span>
                    <span>{selectedSummary.callTrack.customerPhoneNumber || 'N/A'}</span>
                  </div>
                  {getContactName(selectedSummary.callTrack.customerPhoneNumber) && (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="font-medium">Name:</span>
                      <span>{getContactName(selectedSummary.callTrack.customerPhoneNumber)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Bot className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">Assistant:</span>
                    <span>{selectedSummary.callTrack.assistantResponse?.vapiAssistant?.name || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg overflow-hidden">
              <button
                onClick={() => setSelectedSummary(null)}
                className="w-full px-4 py-2 text-left text-gray-100 hover:bg-gray-700 flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Summary
                </span>
                <ChevronUp className="h-4 w-4" />
              </button>
              <div className="px-4 py-2 text-gray-300">
                {selectedSummary.summary}
              </div>
            </div>

            {/* Analysis Summary Section */}
            {selectedSummary.analysisSummary && (
              <div className="bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => setSelectedSummary(null)}
                  className="w-full px-4 py-2 text-left text-gray-100 hover:bg-gray-700 flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Analysis Summary
                  </span>
                  <ChevronUp className="h-4 w-4" />
                </button>
                <div className="px-4 py-2 text-gray-300">
                  <pre className="whitespace-pre-wrap">{selectedSummary.analysisSummary}</pre>
                </div>
              </div>
            )}

            {/* Analysis Success Evaluation Section */}
            <div className="bg-gray-800 rounded-lg overflow-hidden">
              <button
                onClick={() => setSelectedSummary(null)}
                className="w-full px-4 py-2 text-left text-gray-100 hover:bg-gray-700 flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  {selectedSummary.analysisSuccessEvaluation ? (
                    <CheckCircle className="h-4 w-4 text-green-400" />
                  ) : selectedSummary.analysisSuccessEvaluation === false ? (
                    <XCircle className="h-4 w-4 text-red-400" />
                  ) : (
                    <BarChart className="h-4 w-4" />
                  )}
                  Analysis Evaluation
                </span>
                <ChevronUp className="h-4 w-4" />
              </button>
              <div className="px-4 py-2 text-gray-300">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Status:</span>
                  {renderSuccessEvaluation(selectedSummary.analysisSuccessEvaluation)}
                </div>
              </div>
            </div>

            {/* Analysis Structured Data Section */}
            {selectedSummary.analysisStructuredData && (
              <div className="bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => setSelectedSummary(null)}
                  className="w-full px-4 py-2 text-left text-gray-100 hover:bg-gray-700 flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    Structured Data
                  </span>
                  <ChevronUp className="h-4 w-4" />
                </button>
                <div className="px-4 py-2 text-gray-300">
                  <pre className="whitespace-pre-wrap text-sm bg-gray-900 p-3 rounded border overflow-x-auto">
                    {JSON.stringify(JSON.parse(selectedSummary.analysisStructuredData), null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {
              selectedSummary.blockchainHash && (
                <div className="mt-2 text-xs text-gray-600">
                  Hash: {selectedSummary.blockchainHash.slice(0, 10)}...
                  <button onClick={async () => {
                    const res = await fetch(`/solana-api/verifyHash?hash=${selectedSummary.blockchainHash}`);
                    const data = await res.json();
                    alert(data.verified ? '✅ Verified on-chain' : '❌ Not verified');
                  }}>
                    Verify
                  </button>
                </div>
              )
            }

            <div className="bg-gray-800 rounded-lg overflow-hidden">
              <button
                onClick={() => setSelectedSummary(null)}
                className="w-full px-4 py-2 text-left text-gray-100 hover:bg-gray-700 flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Transcript
                </span>
                <ChevronUp className="h-4 w-4" />
              </button>
              <div className="px-4 py-2 max-h-72 overflow-y-auto text-gray-300">
                {selectedSummary.transcript}
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg overflow-hidden">
              <button
                onClick={() => setSelectedSummary(null)}
                className="w-full px-4 py-2 text-left text-gray-100 hover:bg-gray-700 flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <BarChart className="h-4 w-4" />
                  Analysis
                </span>
                <ChevronUp className="h-4 w-4" />
              </button>
              <div className="px-4 py-2 text-gray-300">
                {selectedSummary.analysis ? (
                  <pre className="whitespace-pre-wrap">{selectedSummary.analysis}</pre>
                ) : (
                  <p>No analysis available</p>
                )}
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg overflow-hidden">
              <button
                onClick={() => setSelectedSummary(null)}
                className="w-full px-4 py-2 text-left text-gray-100 hover:bg-gray-700 flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Call Logs
                </span>
                <ChevronUp className="h-4 w-4" />
              </button>
              <div className="h-[300px] overflow-y-auto rounded-md border border-gray-700 p-4 bg-gray-800/50">
                {selectedSummary.callLogs.split('\n').map((line, index) => (
                  <div
                    key={index}
                    className={`py-1 ${
                      line.startsWith('Assistant:') ? 'pl-4 text-blue-400' :
                      line.startsWith('User:') ? 'pl-4 text-emerald-400' : 'text-gray-300'
                    }`}
                  >
                    {line}
                  </div>
                ))}
              </div>
            </div>

            {/* Replace the standard audio element with our custom ThemedAudioPlayer */}
            {/* Recording available — use callId for proxy-based retrieval.
                Falls back to recordingUrl for older records stored before
                Vapi moved to private bucket storage. */}
            {(selectedSummary.callId) && (
              <ThemedAudioPlayer
                callId={selectedSummary.callId}
                recordingUrl={selectedSummary.recordingUrl}
                summaryId={selectedSummary.id}
                artifactType="mono-recording"
              />
            )}
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center p-4 border-t border-gray-800">
          <div className="flex gap-2">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  currentPage === i + 1
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemedCallSummary;